import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FloatInput, FloatNumericInput, FloatSelect, showToast, WizardFooter } from "src/componentes";
import { FormatarCNPJ, FormatarCPF } from "src/hook/Functions";
import { EmpresaBuscarPorUsuario } from "src/services/Entidade.Service";
import {
  EResponsavelSeguroMDFe,
  ETipoEmitenteMDFe,
  EUnidadeMedidaPesoMDFe,
  getLocal,
  MDFeInput,
  responsavelSeguroMDFeOptions,
  STORAGEMDFE_KEY,
  tipoCargaMDFeOptions,
  unidadeMedidaPesoMDFeOptions,
  updateLocal,
} from "../MDFe.Dto";
import { TotaisFormType, TotaisSchema } from "./MDFe.Cadastro.Dto";

const MDFeCadastroTotaisView = () => {
  const navigate = useNavigate();
  const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
  const [empresaCnpj, setEmpresaCnpj] = useState<string>("");

  const isPrestador =
    Number(mdfe?.tipoEmitente) === ETipoEmitenteMDFe.PrestadorServicoTransporte ||
    Number(mdfe?.tipoEmitente) === ETipoEmitenteMDFe.PrestadorServicoCTeGlobalizado;

  const isCargaPropriaComTransp =
    Number(mdfe?.tipoEmitente) === ETipoEmitenteMDFe.TransportadorCargaPropria &&
    mdfe?.tipoTransportador !== null &&
    mdfe?.tipoTransportador !== undefined &&
    mdfe?.tipoTransportador !== ("" as any);

  const totalDocumentos = (mdfe?.municipiosDescarga || []).reduce(
    (acc, m) => acc + (m.documentos ? m.documentos.length : 0),
    0
  );

  const isCargaLotacao =
    (Number(mdfe?.modal) === 1 || !mdfe?.modal) &&
    (isPrestador || isCargaPropriaComTransp) &&
    totalDocumentos === 1;

  const form = useForm<TotaisFormType>({
    resolver: yupResolver(TotaisSchema),
    defaultValues: {
      valorTotalCarga: mdfe?.valorTotalCarga ?? undefined,
      pesoBrutoTotal: mdfe?.pesoBrutoTotal ?? undefined,
      unidadeMedidaPeso:
        mdfe?.unidadeMedidaPeso ?? EUnidadeMedidaPesoMDFe.KG,
      seguros:
        mdfe?.seguros && mdfe.seguros.length > 0
          ? mdfe.seguros.map((s) => ({
              ...s,
              cnpjSeguradora: FormatarCNPJ(s.cnpjSeguradora || ""),
              documentoResponsavel: s.documentoResponsavel
                ? s.documentoResponsavel.replace(/[^A-Za-z0-9]/g, "").length ===
                    11 && !/[A-Za-z]/.test(s.documentoResponsavel)
                  ? FormatarCPF(s.documentoResponsavel)
                  : FormatarCNPJ(s.documentoResponsavel)
                : "",
            }))
          : isPrestador
          ? [
              {
                responsavelSeguro: EResponsavelSeguroMDFe.EmitenteMDFe,
                documentoResponsavel: "",
                nomeSeguradora: "",
                cnpjSeguradora: "",
                numeroApolice: "",
                numeroAverbaCorrente: "",
              },
            ]
          : [],
      produtoPredominante: {
        tipoCarga: mdfe?.produtoPredominante?.tipoCarga ?? 5,
        nomeProduto: mdfe?.produtoPredominante?.nomeProduto ?? "",
        codigoEAN: mdfe?.produtoPredominante?.codigoEAN ?? "",
        ncm: mdfe?.produtoPredominante?.ncm ?? "",
        cepCarregamento: mdfe?.produtoPredominante?.cepCarregamento ?? "",
        cepDescarregamento: mdfe?.produtoPredominante?.cepDescarregamento ?? "",
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "seguros",
  });

  useEffect(() => {
    let isMounted = true;
    EmpresaBuscarPorUsuario()
      .then((res) => {
        if (isMounted && res.data?.success && res.data?.data?.documento) {
          const doc = res.data.data.documento;
          setEmpresaCnpj(doc);
          const currentSeguros = form.getValues("seguros") || [];
          currentSeguros.forEach((s, idx) => {
            if (
              Number(s.responsavelSeguro) === EResponsavelSeguroMDFe.EmitenteMDFe &&
              (!s.documentoResponsavel || s.documentoResponsavel.trim() === "")
            ) {
              form.setValue(
                `seguros.${idx}.documentoResponsavel`,
                FormatarCNPJ(doc)
              );
            }
          });
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [form]);

  const handleVoltar = () => {
    navigate("../documentos");
  };

  const onSubmit = (data: TotaisFormType) => {
    if (isPrestador && (!data.seguros || data.seguros.length === 0)) {
      showToast({
        message:
          "O grupo 'Seguro de Carga' é obrigatório para Prestadores de Serviço de Transporte.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }

    if (data.seguros && data.seguros.length > 0) {
      for (let i = 0; i < data.seguros.length; i++) {
        const seg = data.seguros[i];
        if (
          Number(seg.responsavelSeguro) ===
            EResponsavelSeguroMDFe.ResponsavelContratacao &&
          (!seg.documentoResponsavel || seg.documentoResponsavel.trim() === "")
        ) {
          form.setError(`seguros.${i}.documentoResponsavel`, {
            type: "manual",
            message:
              "Documento (CPF ou CNPJ) do responsável pelo seguro é obrigatório quando o contratante for o responsável (Rejeição 699).",
          });
          showToast({
            message: `Seguro #${i + 1}: Documento (CPF ou CNPJ) do responsável pelo seguro é obrigatório quando o contratante for o responsável (Rejeição 699).`,
            title: "Validação do Seguro",
            variant: "Warning",
          });
          return;
        }

        if (!seg.nomeSeguradora || seg.nomeSeguradora.trim().length < 2) {
          form.setError(`seguros.${i}.nomeSeguradora`, {
            type: "manual",
            message: "Nome da seguradora é obrigatório (Rejeição 699).",
          });
          showToast({
            message: `Seguro #${i + 1}: Nome da seguradora é obrigatório (Rejeição 699).`,
            title: "Validação do Seguro",
            variant: "Warning",
          });
          return;
        }

        if (!seg.cnpjSeguradora || seg.cnpjSeguradora.trim() === "") {
          form.setError(`seguros.${i}.cnpjSeguradora`, {
            type: "manual",
            message: "CNPJ da seguradora é obrigatório (Rejeição 699).",
          });
          showToast({
            message: `Seguro #${i + 1}: CNPJ da seguradora é obrigatório (Rejeição 699).`,
            title: "Validação do Seguro",
            variant: "Warning",
          });
          return;
        }

        if (!seg.numeroApolice || seg.numeroApolice.trim() === "") {
          form.setError(`seguros.${i}.numeroApolice`, {
            type: "manual",
            message:
              "Número da apólice do seguro é obrigatório para o modal rodoviário (Rejeição 699).",
          });
          showToast({
            message: `Seguro #${i + 1}: Número da apólice do seguro é obrigatório para o modal rodoviário (Rejeição 699).`,
            title: "Validação do Seguro",
            variant: "Warning",
          });
          return;
        }

        if (!seg.numeroAverbaCorrente || seg.numeroAverbaCorrente.trim() === "") {
          form.setError(`seguros.${i}.numeroAverbaCorrente`, {
            type: "manual",
            message:
              "Número de averbação do seguro é obrigatório para o modal rodoviário (Rejeição 699).",
          });
          showToast({
            message: `Seguro #${i + 1}: Número de averbação do seguro é obrigatório para o modal rodoviário (Rejeição 699).`,
            title: "Validação do Seguro",
            variant: "Warning",
          });
          return;
        }
      }
    }

    if (isCargaLotacao) {
      const nomeProd = data.produtoPredominante?.nomeProduto?.trim();
      if (!nomeProd) {
        form.setError("produtoPredominante.nomeProduto", {
          type: "manual",
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). Para operação com documento único (carga lotação), informe os dados do produto predominante com os locais de carregamento e descarregamento.",
        });
        showToast({
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). Para operação com documento único (carga lotação), informe os dados do produto predominante com os locais de carregamento e descarregamento.",
          title: "Carga Lotação Obrigatória",
          variant: "Warning",
        });
        return;
      }

      const cepCarreg = data.produtoPredominante?.cepCarregamento
        ? data.produtoPredominante.cepCarregamento.replace(/\D/g, "")
        : "";
      if (!cepCarreg || cepCarreg.length !== 8) {
        form.setError("produtoPredominante.cepCarregamento", {
          type: "manual",
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). CEP de carregamento é obrigatório para carga lotação.",
        });
        showToast({
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). CEP de carregamento é obrigatório para carga lotação.",
          title: "Carga Lotação Obrigatória",
          variant: "Warning",
        });
        return;
      }

      const cepDesc = data.produtoPredominante?.cepDescarregamento
        ? data.produtoPredominante.cepDescarregamento.replace(/\D/g, "")
        : "";
      if (!cepDesc || cepDesc.length !== 8) {
        form.setError("produtoPredominante.cepDescarregamento", {
          type: "manual",
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). CEP de descarregamento é obrigatório para carga lotação.",
        });
        showToast({
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). CEP de descarregamento é obrigatório para carga lotação.",
          title: "Carga Lotação Obrigatória",
          variant: "Warning",
        });
        return;
      }

      // Rejeição 301: O NCM do produto predominante da carga lotação deve ser informado
      const ncmLimpo = data.produtoPredominante?.ncm
        ? data.produtoPredominante.ncm.replace(/\D/g, "")
        : "";
      if (!ncmLimpo) {
        form.setError("produtoPredominante.ncm", {
          type: "manual",
          message:
            "O NCM do produto predominante da carga lotação deve ser informado (Rejeição 301).",
        });
        showToast({
          message:
            "O NCM do produto predominante da carga lotação deve ser informado (Rejeição 301).",
          title: "Carga Lotação Obrigatória",
          variant: "Warning",
        });
        return;
      }
      if (ncmLimpo.length !== 8) {
        form.setError("produtoPredominante.ncm", {
          type: "manual",
          message:
            "NCM do produto predominante deve conter exatamente 8 dígitos numéricos.",
        });
        showToast({
          message:
            "NCM do produto predominante deve conter exatamente 8 dígitos numéricos.",
          title: "Validação NCM",
          variant: "Warning",
        });
        return;
      }
    }

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      valorTotalCarga: data.valorTotalCarga
        ? Number(data.valorTotalCarga)
        : undefined,
      pesoBrutoTotal: data.pesoBrutoTotal
        ? Number(data.pesoBrutoTotal)
        : undefined,
      unidadeMedidaPeso: data.unidadeMedidaPeso
        ? Number(data.unidadeMedidaPeso)
        : EUnidadeMedidaPesoMDFe.KG,
      seguros:
        data.seguros && data.seguros.length > 0
          ? data.seguros.map((s) => ({
              responsavelSeguro: Number(s.responsavelSeguro),
              documentoResponsavel: s.documentoResponsavel
                ? s.documentoResponsavel
                    .replace(/[^A-Za-z0-9]/g, "")
                    .toUpperCase()
                : undefined,
              nomeSeguradora: s.nomeSeguradora.trim(),
              cnpjSeguradora: s.cnpjSeguradora
                ? s.cnpjSeguradora
                    .replace(/[^A-Za-z0-9]/g, "")
                    .toUpperCase()
                : "",
              numeroApolice: s.numeroApolice ? s.numeroApolice.trim() : "",
              numeroAverbaCorrente: s.numeroAverbaCorrente
                ? s.numeroAverbaCorrente.trim()
                : "",
            }))
          : undefined,
      produtoPredominante: data.produtoPredominante?.nomeProduto?.trim()
        ? {
            tipoCarga: Number(data.produtoPredominante.tipoCarga) || 5,
            nomeProduto: data.produtoPredominante.nomeProduto.trim().toUpperCase(),
            codigoEAN: data.produtoPredominante.codigoEAN || undefined,
            ncm: data.produtoPredominante.ncm || undefined,
            cepCarregamento: data.produtoPredominante.cepCarregamento || undefined,
            cepDescarregamento: data.produtoPredominante.cepDescarregamento || undefined,
          }
        : undefined,
    });

    navigate("../pagamento");
  };

  const onError = (errors: any) => {
    if (errors.seguros) {
      showToast({
        message:
          "O grupo 'Seguro de Carga' possui campos obrigatórios não preenchidos (Seguradora, CNPJ, Apólice e Averbação).",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    if (errors.produtoPredominante) {
      const msg =
        errors.produtoPredominante.nomeProduto?.message ||
        errors.produtoPredominante.tipoCarga?.message ||
        errors.produtoPredominante.ncm?.message ||
        errors.produtoPredominante.codigoEAN?.message ||
        errors.produtoPredominante.cepCarregamento?.message ||
        errors.produtoPredominante.cepDescarregamento?.message ||
        "O grupo 'Produto Predominante' possui campos obrigatórios não preenchidos.";
      showToast({
        message: msg,
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    showToast({
      message: "Existem campos obrigatórios não preenchidos nesta etapa.",
      title: "Grupo Obrigatório",
      variant: "Warning",
    });
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      {/* Totais da Carga */}
      <div className="mb-4">
        <div>
          <h5 className="text-primary mb-1">Totais da Carga</h5>
          <small className="text-muted d-block mb-3">
            Valores consolidados de todas as mercadorias transportadas. Caso não informados, a SEFAZ aceita cálculo pelo somatório dos documentos fiscais.
          </small>
        </div>

        <Row className="g-3">
          <Col xs={12} md={4}>
            <FloatNumericInput
              label="Valor Total da Carga (R$)"
              name="valorTotalCarga"
              control={form.control}
              errorsMessage={form.formState.errors.valorTotalCarga}
              decimalScale={2}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatNumericInput
              label="Peso Bruto Total"
              name="pesoBrutoTotal"
              control={form.control}
              errorsMessage={form.formState.errors.pesoBrutoTotal}
              decimalScale={4}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatSelect
              label="Unidade de Medida"
              options={unidadeMedidaPesoMDFeOptions}
              register={form.register("unidadeMedidaPeso", {
                valueAsNumber: true,
              })}
              errorsMessage={form.formState.errors.unidadeMedidaPeso}
            />
          </Col>
        </Row>
      </div>

      {/* Produto Predominante (NT 2025.001 - Regra F55a / Rejeição 726) */}
      <div className="mb-4">
        <div>
          <h5 className="text-primary mb-1">
            Produto Predominante
            {isCargaLotacao && (
              <Badge bg="danger" className="ms-2">
                Obrigatório - Carga Lotação (Rejeição 726)
              </Badge>
            )}
          </h5>
          <small className="text-muted d-block mb-2">
            Obrigatório em operações de Carga Lotação ou quando houver apenas 1 documento vinculado (Regra F55a da SEFAZ).
          </small>
          {isCargaLotacao && (
            <Alert variant="warning" className="py-2 px-3 mb-3 small">
              <strong>Atenção (Rejeição 726):</strong> Esta operação possui apenas 1 documento fiscal vinculado e é classificada como <strong>Carga Lotação</strong>. A SEFAZ exige o preenchimento obrigatório da <strong>Descrição do Produto Predominante</strong>, do <strong>Código NCM</strong> (Rejeição 301), do <strong>CEP de Carregamento</strong> e do <strong>CEP de Descarregamento</strong>.
            </Alert>
          )}
        </div>

        <Row className="g-3">
          <Col xs={12} md={4}>
            <FloatSelect
              label="Tipo de Carga (ANTT) *"
              options={tipoCargaMDFeOptions}
              register={form.register("produtoPredominante.tipoCarga", {
                valueAsNumber: true,
              })}
              errorsMessage={
                form.formState.errors.produtoPredominante?.tipoCarga
              }
            />
          </Col>

          <Col xs={12} md={8}>
            <FloatInput
              label="Descrição do Produto Predominante *"
              inputMask="maiusculas"
              registerField={form.register("produtoPredominante.nomeProduto")}
              errorsMessage={
                form.formState.errors.produtoPredominante?.nomeProduto
              }
              maxLength={120}
            />
          </Col>

          <Col xs={12} md={3}>
            <FloatInput
              label={`Código NCM (8 dígitos) ${isCargaLotacao ? "*" : ""}`}
              inputMask="int"
              maxLength={8}
              registerField={form.register("produtoPredominante.ncm")}
              errorsMessage={form.formState.errors.produtoPredominante?.ncm}
            />
          </Col>

          <Col xs={12} md={3}>
            <FloatInput
              label="Código GTIN/EAN (ou 'SEM GTIN')"
              inputMask="maiusculas"
              maxLength={14}
              registerField={form.register("produtoPredominante.codigoEAN")}
              errorsMessage={form.formState.errors.produtoPredominante?.codigoEAN}
            />
          </Col>

          <Col xs={12} md={3}>
            <FloatInput
              label={`CEP Local Carregamento ${isCargaLotacao ? "*" : ""}`}
              inputMask="cep"
              registerField={form.register("produtoPredominante.cepCarregamento")}
              errorsMessage={
                form.formState.errors.produtoPredominante?.cepCarregamento
              }
            />
          </Col>

          <Col xs={12} md={3}>
            <FloatInput
              label={`CEP Local Descarregamento ${isCargaLotacao ? "*" : ""}`}
              inputMask="cep"
              registerField={form.register(
                "produtoPredominante.cepDescarregamento"
              )}
              errorsMessage={
                form.formState.errors.produtoPredominante?.cepDescarregamento
              }
            />
          </Col>
        </Row>
      </div>

      {/* Seguros de Carga */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="text-primary mb-0">
              Seguro de Carga
              {isPrestador && (
                <Badge bg="warning" text="dark" className="ms-2">
                  Obrigatório
                </Badge>
              )}
            </h5>
            <small className="text-muted">
              {isPrestador
                ? "Obrigatório para Prestador de Serviço de Transporte (Regra G050 da SEFAZ)."
                : "Opcional para transporte próprio / Conforme exigência regulatória"}
            </small>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              append({
                responsavelSeguro: EResponsavelSeguroMDFe.EmitenteMDFe,
                documentoResponsavel: empresaCnpj
                  ? FormatarCNPJ(empresaCnpj)
                  : "",
                nomeSeguradora: "",
                cnpjSeguradora: "",
                numeroApolice: "",
                numeroAverbaCorrente: "",
              })
            }
          >
            + Adicionar Seguro
          </Button>
        </div>

        {fields.length === 0 ? (
          <div className="text-muted py-3">
            Nenhum seguro vinculado a este manifesto.
          </div>
        ) : (
          fields.map((field, index) => (
            <div key={field.id} className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold text-secondary">
                  Apólice de Seguro #{index + 1}
                </span>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => remove(index)}
                >
                  Remover
                </Button>
              </div>

              <Row className="g-3">
                <Col xs={12} md={4}>
                  <FloatSelect
                    label="Responsável pelo Seguro *"
                    options={responsavelSeguroMDFeOptions}
                    register={form.register(
                      `seguros.${index}.responsavelSeguro`,
                      {
                        valueAsNumber: true,
                        onChange: (e) => {
                          const val = Number(e.target.value);
                          if (
                            val === EResponsavelSeguroMDFe.EmitenteMDFe &&
                            empresaCnpj
                          ) {
                            form.setValue(
                              `seguros.${index}.documentoResponsavel`,
                              FormatarCNPJ(empresaCnpj)
                            );
                            form.trigger(
                              `seguros.${index}.documentoResponsavel`
                            );
                          }
                        },
                      }
                    )}
                    errorsMessage={
                      form.formState.errors.seguros?.[index]?.responsavelSeguro
                    }
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label={
                      Number(
                        form.watch(`seguros.${index}.responsavelSeguro`)
                      ) === EResponsavelSeguroMDFe.ResponsavelContratacao
                        ? "Doc. Responsável (CPF/CNPJ) *"
                        : "Doc. Responsável (CPF/CNPJ)"
                    }
                    registerField={form.register(
                      `seguros.${index}.documentoResponsavel`
                    )}
                    errorsMessage={
                      form.formState.errors.seguros?.[index]
                        ?.documentoResponsavel
                    }
                    inputMask="cnpj-cpf"
                    maxLength={18}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label="CNPJ da Seguradora *"
                    registerField={form.register(
                      `seguros.${index}.cnpjSeguradora`
                    )}
                    errorsMessage={
                      form.formState.errors.seguros?.[index]?.cnpjSeguradora
                    }
                    inputMask="cnpj"
                    maxLength={18}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label="Nome da Seguradora *"
                    registerField={form.register(
                      `seguros.${index}.nomeSeguradora`
                    )}
                    errorsMessage={
                      form.formState.errors.seguros?.[index]?.nomeSeguradora
                    }
                    maxLength={30}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label="Número da Apólice *"
                    registerField={form.register(
                      `seguros.${index}.numeroApolice`
                    )}
                    errorsMessage={
                      form.formState.errors.seguros?.[index]?.numeroApolice
                    }
                    maxLength={20}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label="Número de Averbação *"
                    registerField={form.register(
                      `seguros.${index}.numeroAverbaCorrente`
                    )}
                    errorsMessage={
                      form.formState.errors.seguros?.[index]
                        ?.numeroAverbaCorrente
                    }
                    maxLength={40}
                  />
                </Col>
              </Row>
            </div>
          ))
        )}
      </div>

      <WizardFooter onVoltar={handleVoltar} />
    </Form>
  );
};

export default MDFeCadastroTotaisView;
